import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Calendar, Clock, MapPin, RefreshCw, User, Users, Building, Heart, Paperclip, FileText, Image, Leaf, Recycle, MessageCircle, Target, Plus, Sparkles, UserPlus, Wand2, Mic, MicOff, Map as MapIcon, Video, Link2 } from 'lucide-react';
import { isValidMapLink, MAP_LINK_ERROR, isValidMeetingLink, MEETING_LINK_ERROR } from '../utils/mapLinks';
import { TipBtn } from '../components/shared/TipBtn';
import { LangToggle } from '../components/shared/LangToggle';
import { useVoiceToText } from '../hooks/useVoiceToText';
import notify from '../utils/notify';
import { callWithRetry } from '../utils/retry';
import ImageUploadPicker from '../components/shared/ImageUploadPicker';
import { EditLockNote, MaterialChangeModal } from '../components/modals/MaterialChangeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Image pools for MAD action images (no external URLs - use AI generation instead)
const MAD_ACTION_IMAGE_POOLS = {
  wellness: [],
  fitness: [],
  environment: [],
  community: [],
  learning: [],
  team: [],
  other: []
};

const usedImageUrls = new Set();

const generateUniqueImageUrl = (title, category, description = '') => {
  const text = `${(title || '').toLowerCase()} ${(description || '').toLowerCase()}`;
  let imageCategory = 'other';
  if (text.includes('yoga') || text.includes('meditation') || text.includes('mindful') || text.includes('mental')) imageCategory = 'wellness';
  else if (text.includes('walk') || text.includes('step') || text.includes('run') || text.includes('fitness')) imageCategory = 'fitness';
  else if (text.includes('plant') || text.includes('tree') || text.includes('clean') || text.includes('environment') || text.includes('green')) imageCategory = 'environment';
  else if (text.includes('volunteer') || text.includes('community') || text.includes('help') || text.includes('blood')) imageCategory = 'community';
  else if (text.includes('learn') || text.includes('skill') || text.includes('train') || text.includes('teach')) imageCategory = 'learning';
  else if (text.includes('team') || text.includes('collab') || text.includes('together')) imageCategory = 'team';
  const pool = MAD_ACTION_IMAGE_POOLS[imageCategory] || MAD_ACTION_IMAGE_POOLS.other;
  let selected = null;
  for (const img of pool) { if (!usedImageUrls.has(img)) { selected = img; usedImageUrls.add(img); break; } }
  if (!selected) { const all = Object.values(MAD_ACTION_IMAGE_POOLS).flat(); const hash = (title || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0); selected = all[(Date.now() + hash) % all.length]; }
  return selected;
};

export const CreateChallengePage = ({ TopHeader, BottomNav }) => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const editInitial = useRef(null);
  const [joinedCount, setJoinedCount] = useState(0);
  const [materialPrompt, setMaterialPrompt] = useState(null);
  const locked = isEdit && joinedCount > 0;
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [whenType, setWhenType] = useState(null);
  const [showRepeat, setShowRepeat] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [similarActions, setSimilarActions] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [joiningAction, setJoiningAction] = useState(null);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingInstructions, setGeneratingInstructions] = useState(false);
  const [refiningTitle, setRefiningTitle] = useState(false);
  const [instructions, setInstructions] = useState([]);
  const [newInstruction, setNewInstruction] = useState('');
  const [formData, setFormData] = useState({
    title: '', description: '', category: 'Wellness', mode: 'individual',
    duration_days: 14, frequency: 'Daily', event_date: new Date().toISOString().split('T')[0],
    start_time: '09:00', end_time: '10:00', repeat_frequency: 'Does not repeat', location: '', venue_details: '', map_link: '', is_online: false, meeting_link: '',
    is_featured: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false); // keeps Save disabled between success and navigation
  const location = useLocation();
  const [attachments, setAttachments] = useState([]);
  const [aiImagePreview, setAiImagePreview] = useState(null);  const [generatingImage, setGeneratingImage] = useState(false);
  // Manual cover-image upload state. Populated by the shared
  // ImageUploadPicker as soon as the user picks a file. `manualImageUrl`
  // is the server URL after /api/upload resolves; `manualImagePreview`
  // is the base64 data URL shown during upload. If both this and
  // aiImagePreview are set, manual wins at submit time.
  const [manualImageUrl, setManualImageUrl] = useState('');
  const [manualImagePreview, setManualImagePreview] = useState('');
  // Tracks whether an ImageUploadPicker upload is currently in flight.
  // Used to gate the Create Action button and to surface an inline
  // "still uploading" message if the user tries to submit before the
  // POST /api/upload response lands (otherwise manualImageUrl is still
  // '' and the code path silently falls back to a stock-pool image).
  const [uploading, setUploading] = useState(false);

  // Metric-tracking state — only used when the creator turns on the
  // "Track a measurable impact?" toggle. When off, this whole section
  // is a no-op and the created challenge has no `metrics` array (same
  // as every challenge created through this form before today).
  const [trackMetric, setTrackMetric] = useState(false);
  const [availableMetrics, setAvailableMetrics] = useState([]); // [{metric_id, label, unit}]
  const [selectedMetricId, setSelectedMetricId] = useState('');
  const [metricTargetValue, setMetricTargetValue] = useState('');
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  useEffect(() => {
    // Fetch once on mount so the dropdown is populated the moment the
    // user toggles the section on. Fails silently — if the endpoint
    // errors we just keep the toggle off/dropdown empty.
    let cancelled = false;
    (async () => {
      setLoadingMetrics(true);
      try {
        const res = await axios.get(`${API}/impact/metric-definitions`);
        if (!cancelled) setAvailableMetrics(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setAvailableMetrics([]);
      } finally {
        if (!cancelled) setLoadingMetrics(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: c } = await axios.get(`${API}/challenges/${editId}?raw=true`);
        if (cancelled) return;
        const presetCats = ['Wellness', 'Climate', 'Community', 'Career dev', 'Inclusion'];
        const wt = c.challenge_type === 'specific-time' ? 'specific-time' : 'over-time';
        const form = {
          title: c.title || '', description: c.description || '', category: c.category || 'Other', mode: c.mode || 'individual',
          duration_days: c.duration_days || 14, frequency: c.frequency || 'Daily',
          event_date: (c.start_date || new Date().toISOString()).slice(0, 10),
          start_time: c.start_time || '09:00', end_time: c.end_time || '10:00',
          repeat_frequency: c.recurring_schedule || 'Does not repeat', location: c.location || '', venue_details: c.venue_details || '',
          map_link: c.map_link || '', is_online: !!c.is_online, meeting_link: c.meeting_link || '',
          is_featured: !!c.is_featured,
        };
        const metric = (c.metrics || [])[0];
        editInitial.current = {
          form, whenType: wt, showRepeat: !!c.recurring_schedule, image_url: c.image_url || null,
          trackMetric: !!metric, metricId: metric?.metric_id || '', target: metric ? String(c.target_value || '') : '',
        };
        setFormData(form); setWhenType(wt); setShowRepeat(!!c.recurring_schedule);
        if (c.category && !presetCats.includes(c.category)) { setShowCustomCategory(true); setCustomCategory(c.category); }
        if (c.location || c.map_link || c.is_online) setShowOptionalFields(true);
        setInstructions(c.instructions || []);
        setManualImageUrl(c.image_url || '');
        setTrackMetric(!!metric); setSelectedMetricId(metric?.metric_id || ''); setMetricTargetValue(metric ? String(c.target_value || '') : '');
        setJoinedCount(c.joined_count || 0);
        setShowSuggestions(false);
      } catch (err) {
        notify.error(err?.response?.data?.detail || 'Could not load this action.');
        navigate(-1);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, navigate]);

  const buildEditPayload = (imageUrl) => {
    const init = editInitial.current;
    const changed = (k) => formData[k] !== init.form[k];
    const p = {
      title: formData.title.trim(), description: formData.description.trim(), category: formData.category,
      image_url: imageUrl, is_featured: !!formData.is_featured, instructions: instructions.length > 0 ? instructions : null,
    };
    const typeChanged = whenType !== init.whenType;
    if (typeChanged) p.challenge_type = whenType;
    if (changed('mode')) p.mode = formData.mode;
    if (whenType === 'over-time') {
      if (changed('frequency')) p.frequency = formData.frequency;
      if (changed('duration_days') || typeChanged) p.duration_days = formData.duration_days;
      if (typeChanged) { p.start_time = null; p.end_time = null; }
    } else if (typeChanged || changed('event_date') || changed('start_time') || changed('end_time')) {
      p.start_date = `${formData.event_date}T${formData.start_time || '00:00'}:00Z`;
      p.end_date = `${formData.event_date}T${formData.end_time || '23:59'}:59Z`;
      p.start_time = formData.start_time; p.end_time = formData.end_time;
      if (typeChanged) p.duration_days = 1;
    }
    if (changed('location')) p.location = formData.location || null;
    if (changed('map_link')) p.map_link = formData.map_link.trim() || null;
    if (changed('is_online')) p.is_online = !!formData.is_online;
    if (changed('meeting_link')) p.meeting_link = (formData.meeting_link || '').trim() || null;
    if (showRepeat !== init.showRepeat || (showRepeat && changed('repeat_frequency'))) p.recurring_schedule = showRepeat ? formData.repeat_frequency : null;
    if (trackMetric !== init.trackMetric || selectedMetricId !== init.metricId || String(metricTargetValue) !== init.target) {
      const m = availableMetrics.find(x => x.metric_id === selectedMetricId);
      const t = Number(String(metricTargetValue).trim());
      if (trackMetric && m && Number.isFinite(t) && t > 0) {
        p.target_value = Math.max(1, Math.round(t));
        p.metrics = [{ metric_id: m.metric_id, prompt: `How many ${m.unit || 'units'} did you log today?`, applies_when: null }];
      } else if (!trackMetric && init.trackMetric) { p.metrics = []; p.target_value = 1; }
    }
    return p;
  };

  const saveEdit = async (payload, confirm = false) => {
    try {
      await axios.put(`${API}/challenges/${editId}`, { ...payload, confirm_material_change: confirm });
      setMaterialPrompt(null);
      setSaved(true); setShowSuccessToast(true); setTimeout(() => { setShowSuccessToast(false); if (location.state?.fromDetailId === editId) navigate(-1); else navigate(`/challenges/${editId}`, { replace: true }); }, 1200);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === 'material_change_confirmation_required') { setMaterialPrompt({ ...d, payload }); return; }
      notify.error(typeof d === 'string' ? d : (d?.message || 'Failed to save changes.'));
    }
  };

  const voiceChallenge = useVoiceToText({
    onTranscript: (text) => setFormData(prev => ({ ...prev, description: prev.description ? `${prev.description} ${text}` : text })),
  });

  const fetchSimilarActions = useCallback(async () => {
    if (!formData.title || formData.title.length < 3) { setSimilarActions([]); return; }
    setLoadingSimilar(true);
    try {
      const params = new URLSearchParams(); params.append('title', formData.title);
      if (formData.category && formData.category !== 'Other') params.append('category', formData.category);
      if (formData.location && formData.location.length >= 3) params.append('location', formData.location);
      const response = await axios.get(`${API}/challenges/similar?${params.toString()}`);
      setSimilarActions(response.data || []);
    } catch { setSimilarActions([]); }
    finally { setLoadingSimilar(false); }
  }, [formData.title, formData.category, formData.location]);

  useEffect(() => {
    if (formData.title.length < 3) { setSimilarActions([]); return; }
    const timer = setTimeout(() => fetchSimilarActions(), 500);
    return () => clearTimeout(timer);
  }, [formData.title, fetchSimilarActions]);

  const handleJoinAction = async (actionId) => {
    setJoiningAction(actionId);
    try { await axios.post(`${API}/challenges/${actionId}/join`); setSaved(true); setShowSuccessToast(true); setTimeout(() => { setShowSuccessToast(false); navigate(`/challenges/${actionId}`, { replace: true }); }, 1500); }
    catch (error) { notify.error(error?.response?.data?.detail || 'Failed to join Action'); }
    finally { setJoiningAction(null); }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','image/png','image/jpeg','image/jpg','image/gif','image/webp'];
    const valid = files.filter(f => { if (!validTypes.includes(f.type)) { notify.error(`${f.name} is not a supported file type.`); return false; } if (f.size > 10 * 1024 * 1024) { notify.error(`${f.name} is too large (max 10MB).`); return false; } return true; });
    setAttachments(prev => [...prev, ...valid]);
  };

  const handleGenerateDescription = async () => {
    if (!formData.title || formData.title.trim().length < 3) return;
    setGeneratingDescription(true);
    try {
      const res = await callWithRetry(() => axios.post(`${API}/generate-description`, { title: formData.title }));
      setFormData(prev => ({ ...prev, description: res.data.description }));
    }
    catch (err) { notify.error(err?.response?.data?.detail || 'Failed to generate description.'); }
    finally { setGeneratingDescription(false); }
  };

  const DESC_MAX = 2000;
  const [improvingDescription, setImprovingDescription] = useState(false);
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const [titleGenError, setTitleGenError] = useState('');
  const autoTitleTried = useRef(false);

  const handleImproveDescription = async () => {
    const current = (formData.description || '').trim();
    if (current.length < 5) return;
    setImprovingDescription(true);
    try {
      const res = await axios.post(`${API}/rephrase-text`, { text: current.slice(0, DESC_MAX), mode: 'improve' });
      const improved = (res.data?.rephrased_text || '').trim();
      if (improved) setFormData(prev => ({ ...prev, description: improved.slice(0, DESC_MAX) }));
      else notify.error("Couldn't improve the text. Please try again.");
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to improve the text.');
    } finally { setImprovingDescription(false); }
  };

  const generateTitle = async () => {
    const desc = (formData.description || '').trim();
    if (desc.length < 3 || generatingTitle) return;
    setGeneratingTitle(true);
    setTitleGenError('');
    try {
      const res = await axios.post(`${API}/ai/generate-title`, { description: desc.slice(0, DESC_MAX), category: formData.category || null });
      const t = (res.data?.title || '').trim();
      if (t) setFormData(prev => ({ ...prev, title: t }));
      else setTitleGenError("Couldn't generate a title. Please type one.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setTitleGenError(typeof d === 'string' ? d : "Couldn't generate a title. Please type one.");
    } finally { setGeneratingTitle(false); }
  };

  const handleDescriptionBlurAutoTitle = () => {
    if (autoTitleTried.current) return;
    if ((formData.title || '').trim()) return;
    if ((formData.description || '').trim().length < 20) return;
    autoTitleTried.current = true;
    generateTitle();
  };

  const handleRefineTitle = async () => {
    // Wand on the Title field — refines the existing title text. Writes
    // back to the TITLE field only (not the description).
    const current = (formData.title || '').trim();
    if (current.length < 3) return;
    setRefiningTitle(true);
    try {
      const res = await callWithRetry(() => axios.post(`${API}/rephrase-text`, { text: current }));
      const refined = (res.data?.rephrased_text || '').trim();
      if (refined) setFormData(prev => ({ ...prev, title: refined }));
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to refine title.');
    } finally {
      setRefiningTitle(false);
    }
  };

  const handleGenerateInstructions = async () => {
    if (!formData.title || formData.title.trim().length < 3) return;
    setGeneratingInstructions(true);
    try {
      const res = await callWithRetry(() => axios.post(`${API}/generate-instructions`, { title: formData.title, description: formData.description || '', category: formData.category || '' }));
      setInstructions(res.data.instructions || []);
    }
    catch (err) { notify.error(err?.response?.data?.detail || 'Failed to generate instructions.'); }
    finally { setGeneratingInstructions(false); }
  };

  const handleCreateAction = async () => {
    const errors = {};
    if (!formData.title || formData.title.trim().length < 3) errors.title = 'Title must be at least 3 characters';
    if (!formData.description || formData.description.trim().length < 5) errors.description = 'Description must be at least 5 characters';
    // One-time actions must have a picked date. Otherwise the backend
    // falls back to `datetime.now(timezone.utc)` for start_date and
    // silently drops whatever date the user chose.
    if (whenType === 'specific-time' && !formData.event_date) {
      errors.event_date = 'Please pick a date for this one-time session.';
    }
    if (whenType === 'specific-time' && formData.start_time && formData.end_time && formData.end_time <= formData.start_time) {
      errors.time = 'End time must be after start time.';
    }
    if (!isValidMapLink(formData.map_link)) { errors.map_link = MAP_LINK_ERROR; setShowOptionalFields(true); }
    if (formData.is_online && !isValidMeetingLink(formData.meeting_link)) { errors.meeting_link = MEETING_LINK_ERROR; setShowOptionalFields(true); }
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    // Block if a manual upload is still in flight — otherwise
    // manualImageUrl would still be '' at this point and the fallback
    // would silently ship a stock-pool image in place of the user's file.
    if (uploading || (manualImagePreview && !manualImageUrl)) {
      setFormErrors({ image: 'Your image is still uploading — please wait a moment and try again.' });
      return;
    }
    setFormErrors({}); setSubmitting(true);
    try {
      let imageUrl;
      if (manualImageUrl) {
        // Manual upload wins over AI-generated when both are set.
        imageUrl = manualImageUrl;
      } else if (aiImagePreview) {
        // Upload the AI-generated image
        const res = await fetch(aiImagePreview);
        const blob = await res.blob();
        const fd = new FormData(); fd.append('file', blob, 'ai-challenge-image.png');
        const upRes = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        imageUrl = upRes.data.url;
      } else if (isEdit) {
        imageUrl = editInitial.current?.image_url || null;
      } else {
        imageUrl = generateUniqueImageUrl(formData.title, formData.category, formData.description);
      }
      if (isEdit) { await saveEdit(buildEditPayload(imageUrl)); return; }
      // Metric-tracking payload: only added when the creator toggled it
      // on AND picked a metric AND typed a target > 0. If any of those
      // are missing we leave `metrics` and `target_value` off the body,
      // preserving today's default behavior byte-for-byte (backend
      // Challenge model defaults target_value to 1, metrics to []/absent).
      const selectedMetric = availableMetrics.find(m => m.metric_id === selectedMetricId);
      const parsedTarget = Number(String(metricTargetValue).trim());
      const shouldAttachMetric =
        trackMetric &&
        selectedMetric &&
        Number.isFinite(parsedTarget) &&
        parsedTarget > 0;

      const payload = {
        title: formData.title.trim(), description: formData.description.trim(), challenge_type: whenType || 'over-time',
        mode: formData.mode, duration_days: whenType === 'over-time' ? formData.duration_days : 1, category: formData.category,
        location: formData.location || null, venue_details: formData.venue_details || null,
        map_link: (formData.map_link || '').trim() || null,
        is_online: !!formData.is_online, meeting_link: formData.is_online ? ((formData.meeting_link || '').trim() || null) : null,
        start_time: whenType === 'specific-time' ? formData.start_time : null, end_time: whenType === 'specific-time' ? formData.end_time : null,
        recurring_schedule: showRepeat ? formData.repeat_frequency : null, image_url: imageUrl, instructions: instructions.length > 0 ? instructions : null,
        is_featured: !!formData.is_featured,
      };
      // One-time sessions: preserve the user's picked date. Build
      // start/end as full ISO-8601 UTC. end_date is deliberately set to
      // the end-of-day 23:59:59 so it is NEVER identical to start_date
      // (guards against the malformed rows seen in the DB where
      // start_date == end_date to the microsecond, which is impossible
      // out of the backend's own `start + duration_days` code path).
      if (whenType === 'specific-time' && formData.event_date) {
        const startClock = formData.start_time || '00:00';
        const endClock = formData.end_time || '23:59';
        payload.start_date = `${formData.event_date}T${startClock}:00Z`;
        payload.end_date = `${formData.event_date}T${endClock}:59Z`;
      }
      if (shouldAttachMetric) {
        // Backend `ChallengeCreate.target_value` is still typed `int`
        // (we deferred the float-relaxation on target_value earlier).
        // Round on the way out so the request never 422s if a user types
        // a decimal target like "5.5". Progress ledgers (total_progress,
        // raw_value) are already float on the read/write paths.
        payload.target_value = Math.max(1, Math.round(parsedTarget));
        payload.metrics = [{
          metric_id: selectedMetric.metric_id,
          // Auto-generated prompt from the metric's unit. Matches the
          // pattern of the four Nagpur seed prompts we hand-wrote.
          prompt: `How many ${selectedMetric.unit || 'units'} did you log today?`,
          applies_when: null,
        }];
      }
      await axios.post(`${API}/challenges`, payload);
      setSaved(true); setShowSuccessToast(true); setTimeout(() => { setShowSuccessToast(false); navigate('/', { replace: true }); }, 1500);
    } catch (err) { notify.error(err?.response?.data?.detail || 'Failed to create Action.'); }
    finally { setSubmitting(false); }
  };

  const handleGenerateAIImage = async () => {
    if (!formData.title?.trim() && !formData.description?.trim()) { notify.error('Add a title or description first'); return; }
    setGeneratingImage(true); setAiImagePreview(null);
    try {
      const res = await axios.post(`${API}/generate-image`, {
        // Legacy field kept for compatibility with the backend's `description`
        // fallback if any older middleware ever inspects it. The three
        // structured fields below are what the current prompt builder uses.
        post_text: `${formData.title}. ${formData.description}`,
        category: formData.category,
        title: formData.title,
        description: formData.description,
      });
      setAiImagePreview(`data:${res.data.mime_type || 'image/png'};base64,${res.data.image_data}`);
    } catch (err) { notify.error(err?.response?.data?.detail || 'Failed to generate image'); }
    finally { setGeneratingImage(false); }
  };

  const TEAL = '#4DB6AC', ORANGE = '#F28C28', GREEN = '#4CAF8E', RED_ORANGE = '#E76F51', MUTED = '#9CA3AF', BG = '#F4F6F9';
  const isFormValid = [formData.title?.trim()?.length >= 3, formData.description?.trim()?.length >= 5, formData.category].filter(Boolean).length >= 3 && whenType !== null;

  const categories = [
    { id: 'Wellness', label: 'Wellness', Icon: Heart, color: TEAL },
    { id: 'Climate', label: 'Climate', Icon: Leaf, color: GREEN },
    { id: 'Community', label: 'Community', Icon: Users, color: RED_ORANGE },
    { id: 'Career dev', label: 'Career', Icon: Target, color: ORANGE },
    { id: 'Inclusion', label: 'Inclusion', Icon: MessageCircle, color: GREEN },
    { id: 'Other', label: 'Other', Icon: Plus, color: MUTED, isOther: true }
  ];
  const modeOptions = [{ id: 'individual', label: 'Individual', Icon: User }, { id: 'team', label: 'Team', Icon: Users }, { id: 'company', label: 'Company', Icon: Building }, { id: 'community', label: 'Community', Icon: Heart }];
  const durationPresets = [{ label: '7 days', value: 7 }, { label: '14 days', value: 14 }, { label: '21 days', value: 21 }, { label: '30 days', value: 30 }];
  const frequencyOptions = ['Daily', 'Weekdays', 'Weekly'];
  const repeatOptions = ['Does not repeat', 'Daily', 'Weekly', 'Biweekly', 'Monthly'];

  const inputStyle = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box', transition: 'border-color 0.18s' };
  const focusIn = (e) => e.target.style.borderColor = TEAL;
  const focusOut = (e, err) => e.target.style.borderColor = err ? RED_ORANGE : '#E5E7EB';
  const sectionLabel = (text, required) => <span style={{ fontSize: 13, fontWeight: 700, color: '#2C3E50', letterSpacing: '-0.2px' }}>{text}{required && <span style={{ color: RED_ORANGE }}> *</span>}</span>;
  const divider = <div style={{ height: 1, background: '#E5E7EB', margin: '4px 0' }} />;

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} style={{ minHeight: '100vh', background: BG }}>
      <AnimatePresence>
        {showSuccessToast && (
          <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -50 }}
            style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 100, background: '#fff', padding: '10px 22px', borderRadius: 50, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', border: `2px solid ${TEAL}`, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: '#2C3E50' }}>
            <Sparkles size={16} color={TEAL} /> {isEdit ? 'Changes saved!' : 'New Action created!'}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div style={{ background: '#fff', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50, borderBottom: '1px solid #EBEEF2' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', margin: 0 }} data-testid="create-action-heading">{isEdit ? 'Edit Action' : 'Create A New Action'}</h2>
        <button onClick={() => navigate(-1)} data-testid="mad-close-btn" style={{ width: 34, height: 34, background: '#F3F4F6', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} color="#6B7280" /></button>
      </div>

      <div style={{ padding: '16px 18px', paddingBottom: 110 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {isEdit && (
            <div data-testid="edit-joined-banner" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: joinedCount > 0 ? '#FEF8F0' : '#F0FAF9', border: `1px solid ${joinedCount > 0 ? '#F28C2840' : '#4DB6AC40'}`, fontSize: 13, fontWeight: 600, color: joinedCount > 0 ? '#92400E' : '#0F766E' }}>
              <Users size={16} /> {joinedCount === 0 ? 'No one has joined yet. Everything can be edited.' : `${joinedCount} ${joinedCount === 1 ? 'person has' : 'people have'} joined. Date, time and location changes will notify them.`}
            </div>
          )}
          {/* Type Selector */}
          <div>
            {sectionLabel('Type')}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 10 }}>
              {categories.map(cat => {
                const sel = formData.category === cat.id || (cat.isOther && showCustomCategory);
                const CatIcon = cat.Icon;
                return (
                  <button key={cat.id} type="button" data-testid={`cat-${cat.id}`}
                    onClick={() => { if (cat.isOther) { setShowCustomCategory(true); setFormData({...formData, category: customCategory || 'Other'}); } else { setShowCustomCategory(false); setFormData({...formData, category: cat.id}); }}}
                    style={{ padding: '12px 6px', borderRadius: 10, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, border: `1.5px solid ${sel ? TEAL : 'transparent'}`, background: sel ? `${TEAL}10` : '#fff', boxShadow: sel ? `0 0 0 3px ${TEAL}15` : 'none', transition: 'all 0.18s' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: sel ? cat.color : '#F1F3F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CatIcon size={18} color={sel ? '#fff' : cat.color} strokeWidth={2} /></div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: sel ? TEAL : '#4B5563' }}>{cat.label}</span>
                  </button>
                );
              })}
            </div>
            <AnimatePresence>{showCustomCategory && <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ marginTop: 8 }}><input type="text" value={customCategory} autoFocus placeholder="Enter custom type..." onChange={(e) => { setCustomCategory(e.target.value); setFormData({...formData, category: e.target.value || 'Other'}); }} style={{ ...inputStyle, borderColor: MUTED }} onFocus={focusIn} onBlur={focusOut} /></motion.div>}</AnimatePresence>
          </div>
          {divider}
          {isEdit ? (<>
          {/* Title */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              {sectionLabel('Title', true)}
              {/* <TipBtn testId="ai-title-btn" icon={refiningTitle ? RefreshCw : Wand2} tooltip="Refine title" size={36} teal={TEAL} spinning={refiningTitle} disabled={refiningTitle || !formData.title || formData.title.trim().length < 3} onClick={handleRefineTitle} /> */}
            </div>
            <input type="text" data-testid="input-title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Morning Yoga Session" style={{ ...inputStyle, borderColor: formErrors.title ? RED_ORANGE : '#E5E7EB' }} onFocus={focusIn} onBlur={(e) => focusOut(e, formErrors.title)} />
            {formErrors.title && <span style={{ fontSize: 11, color: RED_ORANGE, marginTop: 4, display: 'block' }}>{formErrors.title}</span>}
          </div>
          {/* Similar Actions */}
          <AnimatePresence>
            {showSuggestions && similarActions.length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ background: '#FEF8F0', borderRadius: 10, padding: 14, border: `1px solid ${ORANGE}40` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Sparkles size={14} color={ORANGE} /><span style={{ fontSize: 12, fontWeight: 600, color: '#92400E' }}>Similar actions exist</span></div>
                  <button type="button" onClick={() => setShowSuggestions(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={14} color="#92400E" /></button>
                </div>
                {similarActions.slice(0, 2).map(action => (
                  <div key={action.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: 8, padding: '8px 10px', border: '1px solid #E5E7EB', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#2C3E50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{action.title}</span>
                    <button type="button" onClick={() => handleJoinAction(action.id)} disabled={joiningAction === action.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: TEAL, border: 'none', borderRadius: 6, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}>
                      <UserPlus size={12} /> {joiningAction === action.id ? '...' : 'Join'}
                    </button>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          {/* Description */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              {sectionLabel('Description', true)}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TipBtn testId="ai-desc-btn" icon={generatingDescription ? RefreshCw : Sparkles} tooltip="Generate" size={36} teal={TEAL} spinning={generatingDescription} disabled={generatingDescription || !formData.title || formData.title.trim().length < 3} onClick={handleGenerateDescription} />
                <TipBtn testId="voice-mic-btn" icon={voiceChallenge.voiceRephrasing ? RefreshCw : voiceChallenge.isListening ? MicOff : Mic} tooltip={voiceChallenge.isListening ? 'Stop' : 'Speak'} size={36} disabled={voiceChallenge.voiceRephrasing} spinning={voiceChallenge.voiceRephrasing} pulsing={voiceChallenge.isListening} onClick={voiceChallenge.toggleListening} teal={voiceChallenge.isListening ? '#EF4444' : TEAL} />
                <LangToggle selectedLang={voiceChallenge.selectedLang} setSelectedLang={voiceChallenge.setSelectedLang} teal={TEAL} />
              </div>
            </div>
            <textarea rows={3} data-testid="input-description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="What's this action about?" style={{ ...inputStyle, resize: 'none', fontFamily: 'inherit', borderColor: formErrors.description ? RED_ORANGE : '#E5E7EB' }} onFocus={focusIn} onBlur={(e) => focusOut(e, formErrors.description)} />
            {formErrors.description && <span style={{ fontSize: 11, color: RED_ORANGE, marginTop: 4, display: 'block' }}>{formErrors.description}</span>}
          </div>
          </>) : (<>
          {/* Description (create mode: written first) */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
              {sectionLabel('Description', true)}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TipBtn testId="ai-improve-desc-btn" icon={improvingDescription ? RefreshCw : Sparkles} tooltip="Improve my text" size={36} teal={TEAL} spinning={improvingDescription} disabled={improvingDescription || (formData.description || '').trim().length < 5} onClick={handleImproveDescription} />
                <TipBtn testId="voice-mic-btn" icon={voiceChallenge.voiceRephrasing ? RefreshCw : voiceChallenge.isListening ? MicOff : Mic} tooltip={voiceChallenge.isListening ? 'Stop' : 'Speak'} size={36} disabled={voiceChallenge.voiceRephrasing} spinning={voiceChallenge.voiceRephrasing} pulsing={voiceChallenge.isListening} onClick={voiceChallenge.toggleListening} teal={voiceChallenge.isListening ? '#EF4444' : TEAL} />
                <LangToggle selectedLang={voiceChallenge.selectedLang} setSelectedLang={voiceChallenge.setSelectedLang} teal={TEAL} />
              </div>
            </div>
            <textarea rows={4} maxLength={DESC_MAX} data-testid="input-description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Describe your idea in a few lines: what will people do, when, and why?" style={{ ...inputStyle, resize: 'none', fontFamily: 'inherit', borderColor: formErrors.description ? RED_ORANGE : '#E5E7EB' }} onFocus={focusIn} onBlur={(e) => { focusOut(e, formErrors.description); handleDescriptionBlurAutoTitle(); }} />
            {formErrors.description && <span style={{ fontSize: 11, color: RED_ORANGE, marginTop: 4, display: 'block' }}>{formErrors.description}</span>}
          </div>
          {/* Title (create mode: generated from description) */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              {sectionLabel('Title', true)}
              <TipBtn testId="ai-title-regenerate-btn" icon={generatingTitle ? RefreshCw : Sparkles} tooltip="Regenerate" size={36} teal={TEAL} spinning={generatingTitle} disabled={generatingTitle || (formData.description || '').trim().length < 3} onClick={generateTitle} />
            </div>
            <input type="text" data-testid="input-title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder={generatingTitle ? 'Writing a title...' : 'A short title for your action'} style={{ ...inputStyle, borderColor: formErrors.title ? RED_ORANGE : '#E5E7EB' }} onFocus={focusIn} onBlur={(e) => focusOut(e, formErrors.title)} />
            {titleGenError && <span data-testid="title-gen-error" style={{ fontSize: 11, color: RED_ORANGE, marginTop: 4, display: 'block' }}>{titleGenError}</span>}
            {formErrors.title && <span style={{ fontSize: 11, color: RED_ORANGE, marginTop: 4, display: 'block' }}>{formErrors.title}</span>}
          </div>
          {/* Similar Actions */}
          <AnimatePresence>
            {showSuggestions && similarActions.length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ background: '#FEF8F0', borderRadius: 10, padding: 14, border: `1px solid ${ORANGE}40` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Sparkles size={14} color={ORANGE} /><span style={{ fontSize: 12, fontWeight: 600, color: '#92400E' }}>Similar actions exist</span></div>
                  <button type="button" onClick={() => setShowSuggestions(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={14} color="#92400E" /></button>
                </div>
                {similarActions.slice(0, 2).map(action => (
                  <div key={action.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: 8, padding: '8px 10px', border: '1px solid #E5E7EB', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#2C3E50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{action.title}</span>
                    <button type="button" onClick={() => handleJoinAction(action.id)} disabled={joiningAction === action.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: TEAL, border: 'none', borderRadius: 6, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}>
                      <UserPlus size={12} /> {joiningAction === action.id ? '...' : 'Join'}
                    </button>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          </>)}
          {divider}
          {/* Who can join */}
          <div>
            {sectionLabel('Who can join?')}
            {locked && <EditLockNote testId="lock-note-mode" count={joinedCount} />}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 10, ...(locked ? { pointerEvents: 'none', opacity: 0.55 } : {}) }}>
              {modeOptions.map(mode => { const sel = formData.mode === mode.id; const ModeIcon = mode.Icon; return (
                <button key={mode.id} type="button" data-testid={`mode-${mode.id}`} onClick={() => setFormData({...formData, mode: mode.id})}
                  style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, border: `1.5px solid ${sel ? TEAL : 'transparent'}`, background: sel ? `${TEAL}10` : '#fff', transition: 'all 0.18s' }}>
                  <ModeIcon size={16} color={sel ? TEAL : MUTED} strokeWidth={2} /><span style={{ fontSize: 12, fontWeight: 600, color: sel ? TEAL : '#4B5563' }}>{mode.label}</span>
                </button>
              ); })}
            </div>
          </div>
          {divider}
          {/* Track a measurable impact — optional. When off, the created
              challenge behaves exactly as it did before this section
              existed (no metrics array, target_value defaults to 1 on
              the backend). When on, we attach `metrics[0]` + `target_value`
              to the create payload so the challenge becomes metric-wired
              (same shape as the 4 Nagpur MAD actions). */}
          <div>
            {sectionLabel('Track a measurable impact?')}
            {locked && <EditLockNote testId="lock-note-metric" count={joinedCount} />}
            <div style={{ ...(locked ? { pointerEvents: 'none', opacity: 0.55 } : {}) }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 10 }}>
              {[
                { id: 'off', label: 'No — completion only', selected: !trackMetric },
                { id: 'on',  label: 'Yes — track a metric',  selected: trackMetric  },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`track-metric-${opt.id}`}
                  onClick={() => setTrackMetric(opt.id === 'on')}
                  style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, border: `1.5px solid ${opt.selected ? TEAL : 'transparent'}`, background: opt.selected ? `${TEAL}10` : '#fff', transition: 'all 0.18s' }}
                >
                  <Target size={16} color={opt.selected ? TEAL : MUTED} strokeWidth={2} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: opt.selected ? TEAL : '#4B5563' }}>{opt.label}</span>
                </button>
              ))}
            </div>
            {trackMetric && (
              <div style={{ marginTop: 12, padding: 14, background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#4B5563', marginBottom: 6 }}>Metric</label>
                <select
                  data-testid="track-metric-select"
                  value={selectedMetricId}
                  onChange={(e) => setSelectedMetricId(e.target.value)}
                  disabled={loadingMetrics || availableMetrics.length === 0}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, background: '#fff', outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
                >
                  <option value="">
                    {loadingMetrics ? 'Loading metrics…' : (availableMetrics.length === 0 ? 'No active metrics available' : 'Select a metric')}
                  </option>
                  {availableMetrics.map(m => (
                    <option key={m.metric_id} value={m.metric_id}>{m.label} ({m.unit})</option>
                  ))}
                </select>

                {selectedMetricId && (() => {
                  const m = availableMetrics.find(x => x.metric_id === selectedMetricId);
                  const unit = m?.unit || 'units';
                  return (
                    <>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#4B5563', marginBottom: 6 }}>Target ({unit})</label>
                      <input
                        data-testid="track-metric-target-input"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        step="1"
                        value={metricTargetValue}
                        onChange={(e) => setMetricTargetValue(e.target.value)}
                        placeholder={`e.g. 100`}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                      />
                    </>
                  );
                })()}
              </div>
            )}
            </div>
          </div>
          {divider}
          {/* Timing */}
          <div>
            {sectionLabel('Timing')}
            {locked && <EditLockNote testId="lock-note-timing" count={joinedCount} text="Ongoing / One-time and frequency are locked" />}
            <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', marginTop: 10, border: '1px solid #E5E7EB', ...(locked ? { pointerEvents: 'none', opacity: 0.55 } : {}) }}>
              {[{ id: 'over-time', icon: Calendar, title: 'Ongoing', sub: 'Multi-day habit or challenge' }, { id: 'specific-time', icon: Clock, title: 'One-time', sub: 'Session or event' }].map((opt, i) => {
                const sel = whenType === opt.id; const OptIcon = opt.icon;
                return (<React.Fragment key={opt.id}>
                  {i > 0 && <div style={{ height: 1, background: '#F0F1F3', marginLeft: 56 }} />}
                  <button type="button" data-testid={`timing-${opt.id}`} onClick={() => { setWhenType(opt.id); if (opt.id === 'over-time') setShowRepeat(false); }}
                    style={{ width: '100%', padding: '14px 16px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', background: sel ? `${TEAL}08` : 'transparent' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: sel ? TEAL : '#F1F3F5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><OptIcon size={18} color={sel ? '#fff' : MUTED} /></div>
                    <div><div style={{ fontSize: 13, fontWeight: 700, color: sel ? TEAL : '#2C3E50' }}>{opt.title}</div><div style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>{opt.sub}</div></div>
                  </button>
                </React.Fragment>);
              })}
            </div>
          </div>
          {/* Duration/Frequency fields */}
          <AnimatePresence>
            {whenType === 'over-time' && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ display: 'flex', flexDirection: 'column', gap: 14, overflow: 'hidden' }}>
                <div><span style={{ fontSize: 12, fontWeight: 600, color: '#4B5563', display: 'block', marginBottom: 8 }}>Duration</span><div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{durationPresets.map(p => { const sel = formData.duration_days === p.value; return (<button key={p.value} type="button" onClick={() => setFormData({...formData, duration_days: p.value})} style={{ padding: '7px 14px', borderRadius: 18, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${sel ? TEAL : '#E5E7EB'}`, background: sel ? `${TEAL}12` : '#fff', color: sel ? TEAL : '#4B5563' }}>{p.label}</button>); })}</div></div>
                <div><span style={{ fontSize: 12, fontWeight: 600, color: '#4B5563', display: 'block', marginBottom: 8 }}>Frequency</span><div data-testid="frequency-options" style={{ display: 'flex', gap: 6, ...(locked ? { pointerEvents: 'none', opacity: 0.55 } : {}) }}>{frequencyOptions.map(f => { const sel = formData.frequency === f; return (<button key={f} type="button" onClick={() => setFormData({...formData, frequency: f})} style={{ padding: '7px 14px', borderRadius: 18, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${sel ? TEAL : '#E5E7EB'}`, background: sel ? `${TEAL}12` : '#fff', color: sel ? TEAL : '#4B5563' }}>{f}</button>); })}</div></div>
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {whenType === 'specific-time' && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
                <div><span style={{ fontSize: 12, fontWeight: 600, color: '#4B5563', display: 'block', marginBottom: 6 }}>Date</span><input type="date" value={formData.event_date} onChange={(e) => setFormData({...formData, event_date: e.target.value})} style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
                {formErrors.event_date && <span data-testid="mad-event-date-error" style={{ color: RED_ORANGE, fontSize: 11, marginTop: 4, display: 'block' }}>{formErrors.event_date}</span>}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div><span style={{ fontSize: 12, fontWeight: 600, color: '#4B5563', display: 'block', marginBottom: 6 }}>Start</span><input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} style={inputStyle} onFocus={focusIn} onBlur={focusOut} /></div>
                  <div><span style={{ fontSize: 12, fontWeight: 600, color: '#4B5563', display: 'block', marginBottom: 6 }}>End</span><input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} style={inputStyle} onFocus={focusIn} onBlur={focusOut} /></div>
                </div>
                {formErrors.time && <span data-testid="mad-time-order-error" style={{ color: RED_ORANGE, fontSize: 11, marginTop: -6, display: 'block' }}>{formErrors.time}</span>}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><RefreshCw size={15} color={MUTED} /><span style={{ fontSize: 13, color: '#2C3E50', fontWeight: 500 }}>Repeat?</span></div>
                  <button type="button" onClick={() => setShowRepeat(!showRepeat)} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', background: showRepeat ? TEAL : '#E5E7EB', cursor: 'pointer', position: 'relative', transition: 'all 0.18s' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: showRepeat ? 22 : 2, transition: 'all 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
                  </button>
                </div>
                <AnimatePresence>{showRepeat && <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}><div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{repeatOptions.filter(r => r !== 'Does not repeat').map(f => { const sel = formData.repeat_frequency === f; return (<button key={f} type="button" onClick={() => setFormData({...formData, repeat_frequency: f})} style={{ padding: '6px 12px', borderRadius: 18, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${sel ? TEAL : '#E5E7EB'}`, background: sel ? `${TEAL}12` : '#fff', color: sel ? TEAL : '#4B5563' }}>{f}</button>); })}</div></motion.div>}</AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
          {/* Cover Image — manual upload + AI generation, both always
              available. Manual wins over AI when both are set. */}
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#2C3E50', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}><Sparkles size={14} color={TEAL} /> Cover Image</span>

            {manualImageUrl || manualImagePreview ? (
              <div data-testid="mad-manual-image-preview" style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
                <img src={manualImagePreview || manualImageUrl} alt="Uploaded cover" style={{ width: '100%', height: 240, objectFit: 'cover' }} />
                <button type="button" data-testid="mad-manual-image-remove"
                  onClick={() => { setManualImageUrl(''); setManualImagePreview(''); }}
                  style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <X size={14} />
                </button>
              </div>
            ) : aiImagePreview ? (
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
                <img src={aiImagePreview} alt="AI generated" style={{ width: '100%', height: 240, objectFit: 'cover' }} />
                <button type="button" onClick={() => setAiImagePreview(null)}
                  style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <X size={14} />
                </button>
                <button type="button" onClick={handleGenerateAIImage} disabled={generatingImage}
                  style={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.9)', border: 'none', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                  <RefreshCw size={12} /> Regenerate
                </button>
              </div>
            ) : null}

            {/* Action buttons — always visible so the user can upload
                manually OR generate with AI at any point. */}
            <div style={{ display: 'flex', gap: 10 }}>
              <ImageUploadPicker
                testId="mad-manual-image-btn"
                label="Upload from Gallery"
                disabled={generatingImage}
                style={{ flex: 1 }}
                onUploadStart={(dataUrl) => { setManualImagePreview(dataUrl); setUploading(true); }}
                onUploadComplete={(url) => { setManualImageUrl(url); setManualImagePreview(''); setUploading(false); }}
                onUploadError={() => { setManualImagePreview(''); setUploading(false); }}
              />
              <button type="button" data-testid="ai-challenge-image-btn" onClick={handleGenerateAIImage} disabled={generatingImage}
                style={{ flex: 1, padding: '12px 16px', borderRadius: 12, border: `1.5px dashed ${TEAL}`, background: '#f0faf9', color: TEAL, fontSize: 13, fontWeight: 600, cursor: generatingImage ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: generatingImage ? 0.7 : 1 }}>
                {generatingImage ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</> : <><Sparkles size={14} /> Generate with AI</>}
              </button>
            </div>
          </div>

          {/* Optional fields */}
          <AnimatePresence>
            {whenType && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <button type="button" onClick={() => setShowOptionalFields(!showOptionalFields)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px dashed #D1D5DB', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <ChevronDown size={14} color={MUTED} style={{ transform: showOptionalFields ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s' }} />
                  <span style={{ fontSize: 12, color: MUTED }}>{showOptionalFields ? 'Hide details' : 'Location & attachments'}</span>
                </button>
                <AnimatePresence>
                  {showOptionalFields && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div><span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginBottom: 6, color: MUTED, fontWeight: 500 }}><Video size={12} /> Format</span>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {[{ v: false, label: 'In person', id: 'in-person' }, { v: true, label: 'Online', id: 'online' }].map(o => { const sel = !!formData.is_online === o.v; return (
                            <button key={o.id} type="button" data-testid={`format-${o.id}`} onClick={() => setFormData({ ...formData, is_online: o.v })}
                              style={{ flex: 1, padding: '9px 10px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${sel ? TEAL : '#E5E7EB'}`, background: sel ? `${TEAL}15` : '#FFFFFF', color: sel ? TEAL : '#374151' }}>{o.label}</button>
                          ); })}
                        </div>
                        {formData.is_online && (<div style={{ marginTop: 10 }}><span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginBottom: 6, color: MUTED, fontWeight: 500 }}><Link2 size={12} /> Meeting link</span><input type="url" inputMode="url" data-testid="mad-meeting-link-input" value={formData.meeting_link || ''} onChange={(e) => { setFormData({ ...formData, meeting_link: e.target.value }); if (formErrors.meeting_link) setFormErrors(prev => ({ ...prev, meeting_link: undefined })); }} placeholder="Paste the Zoom, Google Meet or Teams link" style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
                        {formErrors.meeting_link && <span data-testid="mad-meeting-link-error" style={{ color: RED_ORANGE, fontSize: 11, marginTop: 4, display: 'block' }}>{formErrors.meeting_link}</span>}</div>)}
                      </div>
                      <div><span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginBottom: 6, color: MUTED, fontWeight: 500 }}><MapPin size={12} /> Location</span><input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="e.g., Conference Room A" style={inputStyle} onFocus={focusIn} onBlur={focusOut} /></div>
                      <div><span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginBottom: 6, color: MUTED, fontWeight: 500 }}><MapIcon size={12} /> Map link</span><input type="url" inputMode="url" data-testid="mad-map-link-input" value={formData.map_link || ''} onChange={(e) => { setFormData({...formData, map_link: e.target.value}); if (formErrors.map_link) setFormErrors(prev => ({ ...prev, map_link: undefined })); }} placeholder="Paste a Google Maps share link (optional)" style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
                      {formErrors.map_link && <span data-testid="mad-map-link-error" style={{ color: RED_ORANGE, fontSize: 11, marginTop: 4, display: 'block' }}>{formErrors.map_link}</span>}</div>
                      <div><span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginBottom: 6, color: MUTED, fontWeight: 500 }}><Paperclip size={12} /> Attachments</span>
                        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 16, borderRadius: 10, border: '1.5px dashed #D1D5DB', background: '#FAFBFC', cursor: 'pointer' }}>
                          <input type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp" onChange={handleFileSelect} style={{ display: 'none' }} /><Paperclip size={20} color={MUTED} /><span style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>Upload files (max 10MB)</span>
                        </label>
                        {attachments.length > 0 && <div style={{ marginTop: 8 }}>{attachments.map((file, i) => (<div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#F3F4F6', borderRadius: 6, fontSize: 12, marginBottom: 4 }}><span style={{ color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{file.name}</span><button type="button" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={14} color={RED_ORANGE} /></button></div>))}</div>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
          {divider}
          {/* Steps */}
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#2C3E50' }}>Instructions <span style={{ fontWeight: 400, color: MUTED, fontSize: 11 }}>(optional)</span></span>
            {instructions.length > 0 && <div style={{ marginTop: 8, marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>{instructions.map((inst, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                <span style={{ minWidth: 22, height: 22, background: TEAL, color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{idx + 1}</span>
                <input type="text" value={inst} data-testid={`instruction-input-${idx}`} onChange={(e) => setInstructions(prev => prev.map((v, i) => i === idx ? e.target.value : v))} style={{ flex: 1, padding: '2px 4px', border: 'none', fontSize: 13, color: '#2C3E50', background: 'transparent', outline: 'none' }} />
                <button type="button" onClick={() => setInstructions(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={14} color={RED_ORANGE} /></button>
              </div>
            ))}</div>}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden', marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input type="text" value={newInstruction} data-testid="new-instruction-input" onChange={(e) => setNewInstruction(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newInstruction.trim()) { setInstructions(prev => [...prev, newInstruction.trim()]); setNewInstruction(''); } } }} placeholder="Write a instruction..." style={{ flex: 1, padding: '11px 14px', border: 'none', fontSize: 13, outline: 'none', background: 'transparent' }} />
                <div style={{ width: 1, height: 24, background: '#E5E7EB' }} />
                <button type="button" onClick={() => { if (newInstruction.trim()) { setInstructions(prev => [...prev, newInstruction.trim()]); setNewInstruction(''); } }} disabled={!newInstruction.trim()} data-testid="add-instruction-btn" style={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: newInstruction.trim() ? 'pointer' : 'not-allowed', color: newInstruction.trim() ? TEAL : MUTED }}><Plus size={18} strokeWidth={2.5} /></button>
              </div>
              <div style={{ height: 1, background: '#E5E7EB' }} />
              <button type="button" onClick={handleGenerateInstructions} data-testid="generate-instructions-btn" disabled={generatingInstructions || !formData.title || formData.title.trim().length < 3} style={{ width: '100%', padding: '10px 14px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', opacity: (!formData.title || formData.title.trim().length < 3) ? 0.5 : 1 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: generatingInstructions ? '#E5E7EB' : `${TEAL}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {generatingInstructions ? <RefreshCw size={14} color={MUTED} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={14} color={TEAL} />}
                </div>
                <div style={{ textAlign: 'left' }}><div style={{ fontSize: 12, fontWeight: 600, color: TEAL }}>{generatingInstructions ? 'Generating...' : 'Generate with AI'}</div><div style={{ fontSize: 10, color: MUTED }}>Auto-fill instructions from your description</div></div>
              </button>
            </div>
          </div>

          {/* Pin to Featured — amber-highlighted card. Create-time only.
              Persisted as `is_featured`; rendered as a ⭐ badge on the
              MAD card in MADSection.js when true. */}
          <div style={{
            marginTop: 16, padding: 12, borderRadius: 10,
            background: formData.is_featured ? '#FFF8E1' : '#F9FAFB',
            border: `1.5px solid ${formData.is_featured ? '#F59E0B' : '#E5E7EB'}`,
            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
            transition: 'background 0.2s, border-color 0.2s',
          }}
            onClick={() => setFormData({ ...formData, is_featured: !formData.is_featured })}
          >
            <input
              type="checkbox"
              data-testid="input-is-featured"
              checked={!!formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
              style={{ width: 18, height: 18, accentColor: '#F59E0B', cursor: 'pointer', flexShrink: 0 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#78350F' }}>Pin to Featured</span>
              <span style={{ fontSize: 11, color: '#92400E', marginTop: 2 }}>
                Shows a ⭐ badge on this action's card in the feed.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '14px 18px', background: '#fff', borderTop: '1px solid #EBEEF2' }}>
        {formErrors.image && (
          <div
            data-testid="create-mad-image-error"
            style={{ fontSize: 12, color: RED_ORANGE, marginBottom: 8, textAlign: 'center' }}
          >
            {formErrors.image}
          </div>
        )}
        <button onClick={handleCreateAction} disabled={!isFormValid || submitting || uploading || saved} data-testid="create-mad-cta"
          style={{ width: '100%', padding: '14px', borderRadius: 16, border: 'none', background: isFormValid && !submitting ? TEAL : '#E5E7EB', color: isFormValid && !submitting ? '#fff' : MUTED, fontSize: 15, fontWeight: 700, cursor: isFormValid && !submitting ? 'pointer' : 'not-allowed', boxShadow: isFormValid && !submitting ? `0 4px 16px ${TEAL}40` : 'none', transition: 'all 0.2s' }}>
          {isEdit ? (submitting ? 'Saving...' : 'Save Changes') : (submitting ? 'Creating...' : 'Create Action')}
        </button>
      </div>
      {materialPrompt && (
        <MaterialChangeModal
          prompt={materialPrompt}
          onCancel={() => setMaterialPrompt(null)}
          onConfirm={async () => { setSubmitting(true); await saveEdit(materialPrompt.payload, true); setSubmitting(false); }}
          busy={submitting}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse-mic { 0%, 100% { box-shadow: 0 0 0 4px rgba(239,68,68,0.2); } 50% { box-shadow: 0 0 0 8px rgba(239,68,68,0.1); } }
        @keyframes tipFadeDown { from { opacity: 0; transform: translateX(-50%) translateY(4px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
        @keyframes tipFadeUp { from { opacity: 0; transform: translateX(-50%) translateY(-4px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
      `}</style>
    </motion.div>
  );
};
